import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { MCP_CATALOG_MODE } from '../McpCatalogConstant';
import { buildFormDefaultValues, buildServerErrorMessage, buildServerFieldError, formatFormDataForSubmit } from '../handlers/mcpCatalogFormHandler';
import McpCatalogFormHeader from './McpCatalogFormHeader';
import McpCatalogFormFields from './McpCatalogFormFields';
import McpCatalogVersionHistory from './McpCatalogVersionHistory';
import McpCatalogFormSchema from '../schema/McpCatalogFormSchema';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useRevalidateOnTranslation from '@/hooks/useRevalidateOnTranslation';
import { useRecoilValue } from 'recoil';
import { translationVersionAtom } from '@/state/atom/translationAtom';

export default function McpCatalogForm({ mode, item, onSave, onCancel, onDelete, onDirtyChange, historyVersions, hasMoreHistory, isLoadingMoreHistory, onLoadMoreHistory }) {
    useTranslationSubscription();
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(McpCatalogFormSchema),
        defaultValues: buildFormDefaultValues(item)
    });

    const { handleSubmit, formState, setError, errors, trigger } = methods;
    // Rebuild error messages that are already shown once the translation dictionary loads.
    useRevalidateOnTranslation(errors, trigger);
    const { isDirty, isValid } = formState;

    // Backend errors are skipped by useRevalidateOnTranslation (re-validating would wipe them),
    // so rebuild their text from the stored messageCode when the dictionary changes.
    const translationVersion = useRecoilValue(translationVersionAtom);
    useEffect(() => {
        Object.entries(errors || {}).forEach(([field, error]) => {
            if (error?.type !== 'server' || !error.messageCode) return;
            if (error.message === buildServerErrorMessage(error.messageCode, error.params)) return;
            setError(field, buildServerFieldError(error.messageCode, error.params));
        });
        // Only re-run when the dictionary changes, not on every error change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translationVersion]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);

    // Notify parent when dirty state changes so it can update the panel atom
    useEffect(() => {
        if (onDirtyChange) {
            onDirtyChange(isDirty);
        }
    }, [isDirty]);

    /**
     * Called by the parent (McpCatalogManagement) after the service responds.
     * Parent handles the actual API call; this component only coordinates
     * loading state and backend error mapping.
     *
     * @param {object} formData - validated form values
     */
    const handleFormSave = async (formData) => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const catalogData = formatFormDataForSubmit(formData, mode, item);
            await onSave(catalogData, setError);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Confirm before leaving the form when there are unsaved changes (COMMSG044)
    const handleCancel = () => {
        if (isDirty) {
            setIsLeaveDialogOpen(true);
            return;
        }
        onCancel();
    };

    const handleConfirmLeave = () => {
        setIsLeaveDialogOpen(false);
        onCancel();
    };

    return (
        <FormProvider {...methods}>
            <div className="mcp-catalog-form">
                <McpCatalogFormHeader
                    mode={mode}
                    isSubmitting={isSubmitting}
                    isFormValid={isValid}
                    isFormDirty={isDirty}
                    onCancel={handleCancel}
                    onSave={handleSubmit(handleFormSave)}
                    onDelete={onDelete}
                />
                <div className="mcp-catalog-form-scroll">
                    <div className="mcp-catalog-form-body">
                        <McpCatalogFormFields mode={mode} item={item} />
                    </div>
                    {mode === MCP_CATALOG_MODE.EDIT && (
                        <McpCatalogVersionHistory
                            versions={historyVersions}
                            hasMore={hasMoreHistory}
                            isLoadingMore={isLoadingMoreHistory}
                            onLoadMore={onLoadMoreHistory}
                        />
                    )}
                </div>
            </div>

            <ConfirmDialog
                isOpen={isLeaveDialogOpen}
                msgConfirm={trI18n('COMMSG044')}
                okCallBack={handleConfirmLeave}
                closeCallBack={() => setIsLeaveDialogOpen(false)}
            />
        </FormProvider>
    );
}

McpCatalogForm.propTypes = {
    mode: PropTypes.oneOf([MCP_CATALOG_MODE.CREATE, MCP_CATALOG_MODE.EDIT]).isRequired,
    item: PropTypes.object,
    /** onSave(catalogData, setError) — parent handles API call; receives setError to map backend errors */
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    /** onDirtyChange(isDirty) — called when form dirty state changes; used to update panel atom */
    onDirtyChange: PropTypes.func,
    /** Accumulated version history records from server-side pagination */
    historyVersions: PropTypes.array,
    /** Whether more pages are available (cursor present) */
    hasMoreHistory: PropTypes.bool,
    /** True while a See More fetch is in flight */
    isLoadingMoreHistory: PropTypes.bool,
    /** Callback to load the next page of history */
    onLoadMoreHistory: PropTypes.func
};

McpCatalogForm.defaultProps = {
    item: null,
    onDelete: null,
    onDirtyChange: null,
    historyVersions: [],
    hasMoreHistory: false,
    isLoadingMoreHistory: false,
    onLoadMoreHistory: null
};
